import React, { JSX, useEffect, useState } from 'react';
import Header from '../../Layout/Header';
import Sidebar from '../../Layout/Sidebar';
import { useOverviewContext } from '../../context/OverviewContext';
import MarkTopicAsReadButton from '../../components/MarkTopicAsReadButton';
import Breadcrumbs from '../../components/Breadcrumb';
import LearningCoachRoleSchoolCard from '../../components/LearningCoachRoleSchoolCard';
import { useChecklist } from '../../context/ChecklistContext';
import { getCurrentChecklistId } from '../../utils/checklistUtils';
import { FooterMobile } from '../../Layout/FooterMobile';

const LearningCoachRoleDetails: React.FC = () => {
const { formattedStudentNames, students } = useOverviewContext();
const [isExpanded, setIsExpanded] = useState(false);

const toggleExpand = () => {
    setIsExpanded(!isExpanded);
};
const breadcrumbItems = [
    { label: "Home", path: "/" },
    { label: "School Prep Checklist", path: "/school-prep-checklist" },
    { label: "Learning Coach Life", path: "/k12-checklist-category/learning-coach-life" },
    { label: "What does a Learning Coach do?" }, 
];
// Get checklist data & currentChecklistId from ChecklistContext
const { checklistData } = useChecklist();
const currentChecklistId = getCurrentChecklistId(checklistData, "WHAT_DOES_A_LEARNING_COACH_DO");

// GRADE LOGIC
const getLessonDetails = (grade: number | string) => {
    const numericGrade = !isNaN(Number(grade)) ? Number(grade) : grade;
    console.log(typeof grade, numericGrade);

    if (numericGrade === 'K' || (typeof numericGrade === 'number' && numericGrade >= 0 && numericGrade <= 5)) {
        return {
            schoolName: "Elementary School",
            gradeRange : "K-5",
            // title: "Supporting Your Student",
            description: "For younger students, you'll be very hands-on during most of the school day, making sure your student attends all their live online classes with their teachers. You'll also organize course materials, help them complete lessons, and teach core skills. You can find fun ways to include engaging offline activities, too—like trips to the museum or zoo.",
            vidyardUuid: "3eQ9H3UucpScJcmnMu4Yxq",
            additionalContent: (
                <>
                <div className="wp-block-columns is-layout-flex mt-4">
                    <div className="wp-block-column is-layout-flow">
                    <p>
                        <strong>
                        Plan Your Student’s Day
                        </strong>
                    </p>
                    <p>
                        Find a method that works for you in
                        scheduling lessons and managing your
                        time.
                    </p>
                    </div>

                    <div className="wp-block-column is-layout-flow">
                    <p>
                        <strong>
                        Lesson Prep and Grading
                        </strong>
                    </p>
                    <p>
                        Use your student’s independent work
                        time to prep anything they may need
                        for the next lesson and to enter
                        grades or attendance.
                    </p>
                    </div>
                </div>

                <div className="wp-block-columns is-layout-flex">
                    <div className="wp-block-column is-layout-flow">
                    <p>
                        <strong>Enjoy the Process</strong>
                    </p>
                    <p>
                        As a Learning Coach in the early
                        grades, you’ll be there for all your
                        child’s discoveries. Take time to
                        enjoy and celebrate their growth.
                    </p>
                    </div>

                    <div className="wp-block-column is-layout-flow">
                    <p>
                        <strong>Get Teacher Support</strong>
                    </p>
                    <p>
                        Your teacher is there to support
                        you, whether you need help
                        organizing lessons or your child
                        needs extra instruction.
                    </p>
                    </div>
                </div>
                </>
            ),
        };
    } else if (typeof numericGrade === 'number' && numericGrade >= 6 && numericGrade <= 8) {
        return {
            schoolName: "Middle School",
            gradeRange : "6-8",
            description: "As your student progresses to middle school, you'll continue to ensure they participate in live classes and redirect them to schoolwork when distractions arise. You'll actively support your child's growth toward independent learning and work to develop your student's time management skills.",
            vidyardUuid: "f8nChd9S1v814txmAM7CyB",
            additionalContent: (  
            <>
                <div className="wp-block-columns is-layout-flex mt-4">
                    <div className="wp-block-column is-layout-flow">
                    <p>
                        <strong>Help Your Student Organize Their Day </strong>
                    </p>
                    <p> Model good time management by helping your student keep up with their schedule.</p>
                    </div>

                    <div className="wp-block-column is-layout-flow">
                    <p>
                        <strong>
                        Increased Class Time
                        </strong>
                    </p>
                    <p>Students will have more subject-specific teachers and additional class <br />sessions, so they will need less help from you to learn their material.</p>
                    </div>
                </div>

                <div className="wp-block-columns is-layout-flex wp-container-core-columns-is-layout-4 wp-block-columns-is-layout-flex">
                    <div className="wp-block-column is-layout-flow wp-block-column-is-layout-flow">
                        <p><strong><strong>Keep Track of Schoolwork</strong></strong></p>
                        
                        <p>While you won’t need to sit with your student through their lessons, make sure you keep informed of how much time they’re spending on schoolwork.</p>
                    </div>

                    <div className="wp-block-column is-layout-flow wp-block-column-is-layout-flow">
                        <p><strong>Get Teacher Support</strong></p>
                        <p> Assist your student in advocating for themselves when they have questions or need extra support. Teachers are glad to help!
                        </p>
                    </div>
                </div>
                </>
            )
        };
    } else if (typeof numericGrade === 'number' && numericGrade >= 9 && numericGrade <= 12) {
        return {
            schoolName: "High School",
            gradeRange : "9-12",
            description: "As a high school Learning Coach, you’ll support your child in becoming a fully independent learner, helping them sharpen their time management skills, track their class attendance, and motivate them to prioritize their schoolwork within their busy teenage years.",
            vidyardUuid: "vXjVZbVSWiUp2Viy6kvHFC",
            additionalContent:  (
                <>
                <div className="wp-block-columns is-layout-flex mt-4">
                    <div className="wp-block-column is-layout-flow">
                    <p>
                        <strong>Know Your Student’s Schedule </strong>
                    </p>
                    <p> Help your high schooler manage their busy schedule by checking in and reminding them about due dates and Class Connects.</p>
                    </div>

                    <div className="wp-block-column is-layout-flow">
                    <p>
                        <strong>Support Independent Learning</strong>
                    </p>
                    <p>Students at the high school level are responsible for their own work, while Learning Coaches take on more of a support role. Let your student take control of their learning.</p>
                    </div>
                </div>

                <div className="wp-block-columns is-layout-flex wp-container-core-columns-is-layout-4 wp-block-columns-is-layout-flex">
                    <div className="wp-block-column is-layout-flow wp-block-column-is-layout-flow">
                        <p><strong><strong>Check the Gradebook</strong></strong></p>
                        
                        <p>As a Learning Coach, you have access to monitor grades and lesson content. Ask questions to make sure students are making progress and understanding content.</p>
                    </div>

                    <div className="wp-block-column is-layout-flow wp-block-column-is-layout-flow">
                        <p><strong>Get Teacher Support</strong></p>
                        <p>Although teachers will work closely with your student, you can always feel free to reach out to them directly if you have any questions or concerns.</p>
                    </div>
                </div>
                </>
            )
        };
    }
    return { schoolName: "", title: "", description: "", vidyardUuid: "", additionalContent: null };
};

// Group students by gradeRange to display accordian
const gradeGroups = students.reduce((gradeGroups, student) => {
    const studentGradeVal = student.grade.gradeId;
    const lesson = getLessonDetails(studentGradeVal);
    console.log(lesson);
    if (lesson && lesson.gradeRange) {
        const { gradeRange } = lesson;

        if (!gradeGroups[gradeRange]) {
            gradeGroups[gradeRange] = { ...lesson, students: [] };
        }
        gradeGroups[gradeRange].students.push(student);
    }
    return gradeGroups;
}, {} as Record<
    string, 
    { schoolName: string; gradeRange: string; description: string; vidyardUuid: string; additionalContent: JSX.Element; students: typeof students }
>);

return (
    <div className="home-layout">
        <div className="sidebar-wrapper">
        <Sidebar />
        </div>
        <div className="body-wrapper-content">
        <div className="row">
            <div className="col-12">
            <Header />
            <div className="main-content">
                    <div className="row">
                        <div className="col-12">
                            <Breadcrumbs items={breadcrumbItems} />
                        </div>
                    </div>
                    <div className="wp-block-columns is-layout-flex">
                    <div className="wp-block-column is-layout-flow" style={{ flexBasis: "66.66%" }}>
                        <h1 className="wp-block-heading">What does a Learning Coach do?&nbsp;

                        </h1>
                        <h4 className="wp-block-heading">Learning Coaches facilitate, support, and monitor their student’s daily learning process, keeping them on track.&nbsp;</h4>
                        
                        <div style={{ height:"50px" }} aria-hidden="true" className="wp-block-spacer"></div>

                        {/* Call Grade Logic unique student as per gradeRange  */}
                        {Object.values(gradeGroups).map((lesson, index) => (
                            <LearningCoachRoleSchoolCard
                                key={index}
                                schoolName={lesson.schoolName}
                                title={`Supporting ${lesson.students.map(s => s.firstName).join(', ')}`} 
                                description={lesson.description}
                                vidyardUuid={lesson.vidyardUuid}
                                gradeRange={lesson.gradeRange}
                                additionalContent={lesson.additionalContent}
                            />
                        ))}
                        
                        {/* {students.map((student, index) => {
                            const studentGradeVal = student.grade.gradeId;
                            console.log(typeof studentGradeVal);

                            const lesson = getLessonDetails(studentGradeVal); 
                            if (!lesson) return null; 

                            return (
                                <LearningCoachRoleSchoolCard
                                    key={index}
                                    schoolName={lesson.schoolName}
                                    title={`Supporting ${student.firstName}`}
                                    description={lesson.description}
                                    vidyardUuid={lesson.vidyardUuid}
                                    gradeRange={lesson.gradeRange}
                                    additionalContent={lesson.additionalContent}
                                />
                            );
                        })} */}
                        
                    </div>

                        <div className="wp-block-column is-layout-flow" style={{ flexBasis: "33.33%" }}></div>
                    </div>
                     {/* Topic Read Component */}
                     {currentChecklistId && <MarkTopicAsReadButton checklistId={currentChecklistId} /> } 
                        
                </div>
            </div>
        </div>
        </div>
        <FooterMobile />
    </div>
);
};
export default LearningCoachRoleDetails;